/**
 * The warmup SAMPLER — plays an exercise's walk on a sampled instrument.
 *
 * Replaces the oscillator voice of `warmupAudio.js` (which stays for the
 * catalog page's fallback when no instrument is loaded) with one recording per
 * semitone, rendered from Anthony's own library through
 * `scripts/warmups/samples/`. The site plays the pattern note by note, the way
 * a teacher does at the piano, which is what makes every control below
 * possible: a pre-rendered climb is one fixed sequence of keys, a played one
 * can be transposed, dropped an octave, paused or rewound mid-exercise.
 *
 * WHAT IS DECIDED ELSEWHERE. Timing and the walk are pure arithmetic in
 * `warmupStepPlan.js` — `repEvents` says which notes at which beat, `advanceWalk`
 * says which tonic next — and are tested there with no audio. This module owns
 * only "play that buffer at that time" plus the transport.
 *
 * TWO LAYERS, per Anthony (2026-09-03, "accompaniment is necessary, with the
 * options to lower/remove guide vocal"): the GUIDE (the pattern itself) on its
 * own gain — full / low / off — and the ACCOMPANIMENT (the generator's I-chord
 * cue, sustained I, V7 → I turnaround) always on.
 *
 * iOS / Safari. Every AudioContext starts `suspended` and resumes only inside
 * a user gesture, so `ensureContext()` is synchronous and `play()` never
 * awaits before scheduling — the same rule `warmupAudio.js` documents. Tab
 * backgrounding can suspend the context too; `pause()`/`resume()` use
 * suspend/resume deliberately so the clock stops with the sound, and the
 * rep timer is re-armed from the audio clock on resume, not from wall time.
 */
import matrix from '../data/warmupMatrix.json';
import { guideInstrumentFor } from './warmupGuideChoice';
import {
	repEvents,
	startWalk,
	advanceWalk,
	descend as descendWalk,
	nextKey,
	DEFAULT_METER,
	isMeter,
	transposeWalk,
	dropOctave as dropOctaveWalk,
	raiseOctave as raiseOctaveWalk,
	rewindWalk,
	midiToName, DEFAULT_ACCOMPANIMENT, isAccompaniment } from './warmupStepPlan';

const STACCATO_MAX_SECONDS = 0.18;
const GUIDE_GAIN = { full: 1, low: 0.3, off: 0 };
const PIANO_GAIN = 0.7; // was 0.55 — Anthony, 2026-09-08, hearing the full figure: "bring the accompaniment up behind [the guide] just a little"
const LOOKAHEAD_SECONDS = 0.12;
const ROLL_SECONDS = 0.025;

let ctx = null;

/** Shared AudioContext, created lazily, resumed synchronously (the gesture rule). */
export const ensureContext = () => {
	const Ctor = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
	if (!Ctor) return null;
	if (!ctx) {
		try {
			ctx = new Ctor();
		} catch {
			return null;
		}
	}
	if (ctx.state === 'suspended' && typeof ctx.resume === 'function') ctx.resume();
	return ctx;
};

// ---------------------------------------------------------------------------
// Instruments
// ---------------------------------------------------------------------------

const instruments = new Map();

export const instrumentUrl = (name) => `${process.env.PUBLIC_URL || ''}/audio/warmups/${name}`;

/**
 * Load an instrument: fetch its manifest, decode every sample once. Cached per
 * name; concurrent callers share one load. Needs a context — call inside or
 * after a gesture. Rejects (rather than resolving to silence) when a file is
 * missing, so a broken set is loud.
 */
export const loadInstrument = (name = 'piano') => {
	if (instruments.has(name)) return instruments.get(name);
	const a = ensureContext();
	if (!a) return Promise.reject(new Error('No Web Audio in this browser'));
	const base = instrumentUrl(name);
	const p = (async () => {
		const res = await fetch(`${base}/manifest.json`);
		if (!res.ok) throw new Error(`Instrument "${name}" manifest: HTTP ${res.status}`);
		const manifest = await res.json();
		const buffers = new Map();
		await Promise.all(
			manifest.samples.map(async (s) => {
				const r = await fetch(`${base}/${s.file}`);
				if (!r.ok) throw new Error(`Sample ${s.file}: HTTP ${r.status}`);
				const bytes = await r.arrayBuffer();
				const buf = await new Promise((resolve, reject) => {
					// Safari's decodeAudioData is callback-only in older versions.
					const maybe = a.decodeAudioData(bytes, resolve, reject);
					if (maybe && typeof maybe.then === 'function') maybe.then(resolve, reject);
				});
				buffers.set(s.midi, buf);
			})
		);
		const midis = [...buffers.keys()].sort((x, y) => x - y);
		return { name, manifest, buffers, lo: midis[0], hi: midis[midis.length - 1] };
	})();
	instruments.set(name, p);
	p.catch(() => instruments.delete(name));
	return p;
};

/** The recording to use for a note: exact when we have it, else the nearest, pitch-shifted. */
const sampleFor = (inst, midi) => {
	if (inst.buffers.has(midi)) return { buffer: inst.buffers.get(midi), sampleMidi: midi };
	const clamped = Math.max(inst.lo, Math.min(inst.hi, midi));
	return { buffer: inst.buffers.get(clamped), sampleMidi: clamped };
};

const rate = (midi, sampleMidi) => Math.pow(2, (midi - sampleMidi) / 12);

/** One played note. A glide bends the recording's rate across the note's length, like a pitch wheel. */
const playNote = (a, inst, dest, { midi, t0, dur, gain = 1, glideTo = null }) => {
	const { buffer, sampleMidi } = sampleFor(inst, midi);
	const src = a.createBufferSource();
	src.buffer = buffer;
	src.playbackRate.setValueAtTime(rate(midi, sampleMidi), t0);
	if (glideTo != null) src.playbackRate.linearRampToValueAtTime(rate(glideTo, sampleMidi), t0 + dur);
	const g = a.createGain();
	g.gain.setValueAtTime(gain, t0);
	g.gain.setTargetAtTime(0.0001, t0 + dur, 0.035);
	src.connect(g).connect(dest);
	src.start(t0);
	src.stop(t0 + dur + 0.3);
	src.midi = midi; // for the harness and the tests; harmless in the browser
	src.layer = dest.layer || null;
	return src;
};

/** The unpitched click — kept from the synth: a 40 ms noise burst with a fast decay. */
const tick = (a, dest, t0) => {
	const b = a.createBuffer(1, Math.floor(a.sampleRate * 0.04), a.sampleRate);
	const d = b.getChannelData(0);
	for (let i = 0; i < d.length; i += 1) d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (a.sampleRate * 0.006));
	const s = a.createBufferSource();
	s.buffer = b;
	const g = a.createGain();
	g.gain.value = 0.35;
	s.connect(g).connect(dest);
	s.start(t0);
	s.tick = true;
	return s;
};

// ---------------------------------------------------------------------------
// The player
// ---------------------------------------------------------------------------

/**
 * Create a player. One per surface; it owns the gain graph and the transport.
 *
 *   const p = createPlayer({ onState });
 *   await p.load();                     // inside/after a gesture
 *   p.play(exercise, { startTonic: 60, top: 67, bpm: 80, countIn: true });
 *   p.pause(); p.resume(); p.rewind(); p.back(); p.next();
 *   p.transpose(-2); p.dropOctave(); p.raiseOctave(); p.descend();
 *   p.setTempo(72); p.setGuideLevel('low'); p.stop();
 *
 * `onState(state)` fires on every change with the readout the UI renders:
 * { status, exerciseId, tonic, tonicName, top, topName, phase, rep, bpm,
 *   guideLevel, octaveShift, countingIn, loaded }.
 * `onEnd(exerciseId)` fires when a walk finishes (after Descend reaches the
 * start tonic) — the caller decides what plays next.
 */
/**
 * The guide-set setting: 'auto' picks per exercise (warmupGuideChoice — slides
 * and long holds on the sustained set, the rest on the piano; Anthony,
 * 2026-09-07); 'voice' / 'piano' force one for the session.
 */
export const VOICES = ['auto', 'voice', 'piano'];

export const createPlayer = ({ instrument = 'piano', voiceInstrument = 'voice', onState = () => {}, onEnd = () => {} } = {}) => {
	let a = null;
	let inst = null;
	let master = null;
	let guideGain = null;
	let pianoGain = null;

	// The second sample set, for the GUIDE only (2026-09-06): a sustained
	// voice — Anthony's own render — so a siren or slide bends a held tone,
	// not a decaying piano. The accompaniment and the count-in stay on the
	// piano. Optional: a bundle without the set falls back to the piano and
	// says so in state (`voiceAvailable`), so the control can grey out.
	let voiceInst = null;
	let voice = 'auto'; // the teacher's setting: 'auto' | 'voice' | 'piano'
	let voiceAvailable = null; // null until load; false when the set is missing
	/** What the guide is actually playing on for the CURRENT exercise. */
	const effectiveVoice = () => (voice === 'auto' ? (exercise ? guideInstrumentFor(exercise) : 'piano') : voice);
	const guideInst = () => (effectiveVoice() === 'voice' && voiceInst ? voiceInst : inst);

	let exercise = null;
	let walk = null;
	let bpm = 80;
	let guideLevel = 'full';
	let meter = DEFAULT_METER; // beats per bar — see repBars
	let accompaniment = DEFAULT_ACCOMPANIMENT; // 'simple' | 'full' — see fullPiano
	let status = 'idle'; // idle | loading | playing | paused | done
	let countingIn = false;
	let once = false;
	let sources = [];
	let repTimer = null;
	let repStartAt = 0;
	let repEndAt = 0;
	// The walk of the rep the teacher is HEARING while the next one is queued.
	// onRepEnd fires LOOKAHEAD_SECONDS before a rep's audio ends and schedules
	// the next rep, so for that last tenth of a second `walk` describes a rep
	// nobody has heard yet. A tap landing there must apply to the audible rep
	// and re-queue the next, or the queued rep plays at the OLD key — one more
	// pass through the top, which is exactly the complaint (2026-09-06). Set
	// only by onRepEnd; cleared by anything that schedules from "now".
	let prevWalk = null;
	let pausedRemaining = 0;

	const emit = () => {
		onState({
			status,
			exerciseId: exercise ? exercise.id : null,
			tonic: walk ? walk.tonic : null,
			tonicName: walk ? midiToName(walk.tonic) : '',
			top: walk ? walk.top : null,
			topName: walk ? midiToName(walk.top) : '',
			startTonic: walk ? walk.startTonic : null,
			phase: walk ? walk.phase : null,
			rep: walk ? walk.rep : 0,
			done: walk ? walk.done : false,
			bpm,
			guideLevel,
			meter,
			accompaniment,
			octaveShift: walk ? walk.octaveShift : 0,
			countingIn,
			loaded: Boolean(inst),
			voice,
			voiceEffective: voiceInst ? effectiveVoice() : 'piano',
			voiceAvailable,
			// What the guide set IS, from its manifest ("Clarinet"), so the
			// control names the sound rather than a folder. Null until loaded.
			voiceLabel: voiceInst && voiceInst.manifest ? voiceInst.manifest.label || null : null,
		});
	};

	const graph = () => {
		if (master) return true;
		a = ensureContext();
		if (!a) return false;
		master = a.createGain();
		master.gain.value = 0.9;
		guideGain = a.createGain();
		guideGain.gain.value = GUIDE_GAIN[guideLevel];
		guideGain.layer = 'guide';
		pianoGain = a.createGain();
		pianoGain.gain.value = PIANO_GAIN;
		pianoGain.layer = 'piano';
		guideGain.connect(master);
		pianoGain.connect(master);
		master.connect(a.destination);
		return true;
	};

	const load = async () => {
		if (!graph()) throw new Error('No Web Audio in this browser');
		if (inst) return inst;
		status = status === 'idle' ? 'loading' : status;
		emit();
		try {
			inst = await loadInstrument(instrument);
			try {
				voiceInst = await loadInstrument(voiceInstrument);
				voiceAvailable = true;
			} catch (err) {
				voiceInst = null;
				voiceAvailable = false;
			}
		} finally {
			if (status === 'loading') status = 'idle';
			emit();
		}
		return inst;
	};

	const clearScheduled = () => {
		sources.forEach((s) => {
			try {
				s.stop();
			} catch {
				/* already ended */
			}
		});
		sources = [];
		clearTimeout(repTimer);
		repTimer = null;
	};

	const armRepEnd = (seconds) => {
		clearTimeout(repTimer);
		repTimer = setTimeout(onRepEnd, Math.max(0, seconds - LOOKAHEAD_SECONDS) * 1000);
	};

	/** Schedule one rep of the current exercise at the current tonic, starting at audio time t0. */
	const scheduleRep = (t0) => {
		// The full figure ends each rep on the V7 of the key the walk goes to next;
		// a single "hear it" rep resolves to its own key instead.
		const nextTonic = once ? walk.tonic : advanceWalk(walk).tonic;
		const { beats, guide, piano } = repEvents(exercise, walk.tonic, matrix, meter, { accompaniment, nextTonic });
		const beat = 60 / bpm;
		const keep = [];
		guide.forEach((ev) => {
			const at = t0 + ev.at * beat;
			if (ev.tick) {
				keep.push(tick(a, guideGain, at));
				return;
			}
			const dur = ev.staccato ? Math.min(ev.dur * beat, STACCATO_MAX_SECONDS) : ev.dur * beat;
			keep.push(playNote(a, guideInst(), guideGain, { midi: ev.midi, t0: at, dur, glideTo: ev.glideTo }));
		});
		piano.forEach((ev) => {
			const at = t0 + ev.at * beat;
			const dur = ev.staccato ? Math.min(ev.dur * beat, 0.12) : ev.dur * beat;
			const perNote = 0.7 / Math.sqrt(ev.midis.length);
			// a rolled chord: low to high, ROLL_SECONDS apart, the way a pianist gives a key
			[...ev.midis].sort((x, y) => x - y).forEach((m, i) => keep.push(playNote(a, inst, pianoGain, { midi: m, t0: at + (ev.roll ? i * ROLL_SECONDS : 0), dur, gain: perNote })));
		});
		sources = keep;
		repStartAt = t0;
		repEndAt = t0 + beats * beat;
		armRepEnd(repEndAt - a.currentTime);
	};

	const onRepEnd = () => {
		if (status !== 'playing') return;
		if (walk.done || once) {
			status = 'done';
			sources = [];
			emit();
			onEnd(exercise.id);
			return;
		}
		prevWalk = walk;
		walk = advanceWalk(walk);
		scheduleRep(repEndAt);
		emit();
	};

	/** True while the next rep is scheduled but its first note has not sounded. */
	const queuedNotStarted = () => status === 'playing' && prevWalk !== null && a.currentTime < repStartAt;

	/** Replace the queued rep with one built from the current walk, same start time. */
	const requeue = () => {
		const at = repStartAt;
		clearScheduled();
		scheduleRep(at);
	};

	/** The generator's count-in: a bar of tonic clicks before the first rep. */
	const scheduleCountIn = (t0) => {
		const beat = 60 / bpm;
		const n = matrix.score.countInBeats;
		for (let k = 0; k < n; k += 1) {
			sources.push(playNote(a, inst, pianoGain, { midi: walk.tonic, t0: t0 + k * beat, dur: 0.12, gain: 0.6 }));
		}
		return t0 + n * beat;
	};

	/**
	 * `once: true` plays a single rep at the start tonic and stops — the catalog
	 * page's "hear it" mode, the sampled successor of warmupAudio.playPattern.
	 */
	const play = (ex, { startTonic = 60, top = null, span = null, climbPast, bpm: tempo = null, countIn = false, once: single = false } = {}) => {
		if (!graph() || !inst) throw new Error('Instrument not loaded — call load() first');
		clearScheduled();
		exercise = ex;
		once = single;
		bpm = tempo || ex.bpm || 80;
		walk = startWalk({ startTonic, top, span: single ? 0 : span, climbPast });
		status = 'playing';
		prevWalk = null;
		let t0 = a.currentTime + 0.08;
		countingIn = countIn;
		if (countIn) {
			t0 = scheduleCountIn(t0);
			const beat = 60 / bpm;
			setTimeout(() => {
				countingIn = false;
				emit();
			}, matrix.score.countInBeats * beat * 1000);
		}
		scheduleRep(t0);
		emit();
	};

	const pause = () => {
		if (status !== 'playing') return;
		status = 'paused';
		clearTimeout(repTimer);
		repTimer = null;
		pausedRemaining = repEndAt - a.currentTime;
		if (typeof a.suspend === 'function') a.suspend();
		emit();
	};

	const resume = () => {
		if (status !== 'paused') return;
		status = 'playing';
		if (typeof a.resume === 'function') a.resume();
		// The audio clock did not move while suspended, so the scheduled sources
		// and this timer pick up exactly where they stopped.
		armRepEnd(pausedRemaining);
		emit();
	};

	/** Restart the current rep from its first beat (the accompaniment cue included). */
	const rewindRep = () => {
		if (!walk || status === 'idle') return;
		clearScheduled();
		if (status === 'paused' && typeof a.resume === 'function') a.resume();
		status = 'playing';
		prevWalk = null;
		scheduleRep(a.currentTime + 0.08);
		emit();
	};

	/** Previous key: a step back down the walk, or the start if we are on it. */
	const back = () => {
		if (!walk || status === 'idle') return;
		if (a.currentTime - repStartAt > 1.5) return rewindRep();
		const limit = walk.limit == null ? walk.top : walk.limit;
		const prev = walk.phase === 'down' ? Math.min(limit, walk.tonic + 1) : Math.max(walk.startTonic, walk.tonic - 1);
		walk = { ...walk, tonic: prev, phase: walk.phase === 'down' ? 'down' : prev >= limit ? 'hold' : 'up', done: false };
		return rewindRep();
	};

	/**
	 * Next key now, without waiting for the rep to end — and at the top, ONE
	 * MORE (see nextKey). Applied to the rep the teacher is hearing, never to a
	 * rep that is queued but silent.
	 */
	const next = () => {
		if (!walk || status === 'idle') return;
		walk = nextKey(queuedNotStarted() ? prevWalk : walk);
		return rewindRep();
	};

	// Transpose and octave apply from the NEXT rep; the current one finishes.
	// When the next rep is already queued, it IS the next rep — re-queue it.
	const transpose = (n) => {
		if (!walk) return;
		walk = transposeWalk(walk, n);
		if (queuedNotStarted()) requeue();
		emit();
	};
	const dropOctave = () => {
		if (!walk) return;
		walk = dropOctaveWalk(walk);
		if (queuedNotStarted()) requeue();
		emit();
	};
	const raiseOctave = () => {
		if (!walk) return;
		walk = raiseOctaveWalk(walk);
		if (queuedNotStarted()) requeue();
		emit();
	};
	// Descend takes effect at the next rep. If that rep is already queued at
	// the hold key, come down from the rep being heard and re-queue.
	const descend = () => {
		if (!walk) return;
		if (queuedNotStarted()) {
			walk = advanceWalk(descendWalk(prevWalk));
			requeue();
		} else {
			walk = descendWalk(walk);
		}
		emit();
	};

	const rewind = () => {
		if (!walk || status === 'idle') return;
		walk = rewindWalk(walk);
		rewindRep();
	};

	const setTempo = (b) => {
		bpm = Math.max(30, Math.min(200, Number(b) || bpm));
		emit();
	};

	// The meter applies from the NEXT rep; the one sounding keeps its bars. If
	// the next rep is already queued, it IS the next rep — re-queue it.
	const setMeter = (n) => {
		if (!isMeter(n) || n === meter) return;
		meter = n;
		if (walk && queuedNotStarted()) requeue();
		emit();
	};

	// The accompaniment figure. Applies from the NEXT rep, like the meter.
	const setAccompaniment = (s) => {
		if (!isAccompaniment(s) || s === accompaniment) return;
		accompaniment = s;
		if (walk && queuedNotStarted()) requeue();
		emit();
	};

	// Which set the guide sings on. Applies from the NEXT rep; a queued rep is
	// re-queued. Choosing 'voice' with no voice set loaded keeps the piano.
	const setVoice = (name) => {
		if (!VOICES.includes(name) || name === voice) return;
		voice = name;
		if (walk && queuedNotStarted()) requeue();
		emit();
	};

	const setGuideLevel = (level) => {
		if (!(level in GUIDE_GAIN)) return;
		guideLevel = level;
		if (guideGain) guideGain.gain.setTargetAtTime(GUIDE_GAIN[level], a.currentTime, 0.02);
		emit();
	};

	const stop = () => {
		clearScheduled();
		if (a && a.state === 'suspended' && typeof a.resume === 'function') a.resume();
		status = 'idle';
		walk = null;
		exercise = null;
		countingIn = false;
		emit();
	};

	const getState = () => ({ status, walk, bpm, guideLevel, meter, accompaniment, voice, voiceEffective: effectiveVoice(), voiceAvailable, exercise });

	return { load, play, pause, resume, rewind, back, next, transpose, dropOctave, raiseOctave, descend, setTempo, setGuideLevel, setMeter, setAccompaniment, setVoice, stop, getState };
};

/** Test seam. */
export const _resetSamplerForTests = () => {
	ctx = null;
	instruments.clear();
};
